import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { getPathName, PLC_PATHS } from '../contracts/pressHMI.schema';
import TcAdsWebService from '../TcAdsWebService';

const ADS_NET_ID = '';
const ADS_PORT = '851';
const ADS_SUM_READ = 0xF080;
const ADS_SUM_WRITE = 0xF081;
const ADS_SUM_READ_WRITE = 0xF082;
const ADS_TIMEOUT_MS = 500;
const ADS_POLL_INTERVAL_MS = 500;

const TYPE_MAP = {
  BYTE: { size: 1, read: 'readBYTE', write: 'writeBYTE' },
  BOOL: { size: 1, read: 'readBOOL', write: 'writeBOOL' },
  WORD: { size: 2, read: 'readWORD', write: 'writeWORD' },
  UINT: { size: 2, read: 'readWORD', write: 'writeWORD' },
  DWORD: { size: 4, read: 'readDWORD', write: 'writeDWORD' },
  SINT: { size: 1, read: 'readSINT', write: 'writeSINT' },
  INT: { size: 2, read: 'readINT', write: 'writeINT' },
  DINT: { size: 4, read: 'readDINT', write: 'writeDINT' },
  TIME: { size: 4, read: 'readDWORD', write: 'writeDWORD' },
  REAL: { size: 4, read: 'readREAL', write: 'writeREAL' },
  LREAL: { size: 8, read: 'readLREAL', write: 'writeLREAL' },
  STRING: { read: 'readString', write: 'writeString' },
};

// Cache TwinCAT symbol handles per machine URL so remounts do not re-resolve them.
const handleCacheByUrl = new Map();

const getHandleCache = (url) => {
  if (!handleCacheByUrl.has(url)) {
    handleCacheByUrl.set(url, new Map());
  }

  return handleCacheByUrl.get(url);
};

const buildHandlesByName = (handles) => handles.reduce(
  (accumulator, handle) => ({ ...accumulator, [handle.name]: handle }),
  {},
);

const TEST_DEFAULTS_BY_HANDLE = {
  [getPathName(PLC_PATHS.status.safetyOk)]: true,
  [getPathName(PLC_PATHS.status.airChillerOn)]: true,
  [getPathName(PLC_PATHS.status.pressInputs.airFeedback)]: true,
  [getPathName(PLC_PATHS.status.pressInputs.chillerFeedback)]: true,
  [getPathName(PLC_PATHS.status.pressInputs.heaterEmergencySwitch)]: true,
};

const currentValueForTestHandle = (handle) => (
  TEST_DEFAULTS_BY_HANDLE[handle.name] ?? {
    BYTE: 0,
    BOOL: false,
    WORD: 0,
    DWORD: 0,
    SINT: 0,
    INT: 0,
    DINT: 0,
    TIME: 0,
    REAL: 0,
    LREAL: 0,
    STRING: '',
  }[handle.type]
);

const getHandleSize = ({ type, size }, value) => {
  if (type === 'STRING') {
    return size || String(value || '').length || 1;
  }

  return TYPE_MAP[type]?.size;
};

const toResponseError = (error) => {
  if (!error) {
    return new Error('Unknown PLC communication error');
  }

  if (error.getTypeString() === 'TcAdsWebService.ResquestError') {
    return new Error(`PLC request failed: ${error.statusText} (${error.status})`);
  }

  if (error.getTypeString() === 'TcAdsWebService.Error') {
    return new Error(`PLC error: ${error.errorMessage} (${error.errorCode})`);
  }

  return new Error('Unknown PLC communication error');
};

const readHandleValue = (reader, handle) => {
  if (handle.type === 'STRING') {
    return reader.readString(handle.size || 255);
  }

  return reader[TYPE_MAP[handle.type].read]();
};

const writeHandleValue = (writer, handle, value, size) => {
  if (handle.type === 'STRING') {
    writer.writeString(value, size);
    return;
  }

  writer[TYPE_MAP[handle.type].write](value);
};

const createReadWritePromise = (client, {
  indexGroup,
  indexOffset,
  responseLength,
  requestData,
  timeoutMessage,
}) => new Promise((resolve, reject) => {
  client.readwrite(
    ADS_NET_ID,
    ADS_PORT,
    indexGroup,
    indexOffset,
    responseLength,
    requestData,
    (response) => {
      if (!response || response.isBusy) {
        return;
      }

      if (response.hasError) {
        reject(toResponseError(response.error));
        return;
      }

      resolve(response);
    },
    null,
    ADS_TIMEOUT_MS,
    () => reject(new Error(timeoutMessage)),
    true,
  );
});

export default function usePLCStore({
  plcUrl,
  handles,
  test = false,
}) {
  // External-store snapshot consumed by useSyncExternalStore in the provider.
  const state = useRef({ ready: false, values: {}, error: null });
  // Subscribers are notified whenever the snapshot changes.
  const listeners = useRef(new Set());
  // One ADS web service client per mounted store.
  const client = useRef(null);
  // Poll loop uses a self-scheduling timeout so reads never overlap.
  const pollTimeoutId = useRef(null);
  // Resolved PLC symbol handles keyed by full PLC path.
  const handleIds = useRef({});
  // Serialize writes so operator actions are applied in click order.
  const writeQueue = useRef(Promise.resolve());

  const handlesByName = useMemo(() => buildHandlesByName(handles), [handles]);

  const emitChange = useCallback(() => {
    listeners.current.forEach((listener) => listener());
  }, []);

  const updateState = useCallback((updater) => {
    const nextState = updater(state.current);
    if (nextState === state.current) {
      return;
    }

    state.current = nextState;
    emitChange();
  }, [emitChange]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      handles.forEach((handle) => {
        if (handle.type === 'STRING' && !handle.size) {
          // eslint-disable-next-line no-console
          console.error(
            `[usePLCStore] STRING handle "${handle.name}" has no size defined.`
            + ' Reads will silently truncate to 1 character.'
            + ' Add a size property to the createPath() call.',
          );
        }
      });
    }

    if (!plcUrl) {
      updateState((current) => ({
        ...current,
        ready: false,
        error: 'Unsupported press',
      }));
      return () => {};
    }

    updateState((current) => {
      const nextValues = handles.reduce((accumulator, handle) => {
        if (current.values[handle.name] === undefined) {
          return accumulator;
        }

        return {
          ...accumulator,
          [handle.name]: current.values[handle.name],
        };
      }, {});

      if (Object.keys(nextValues).length === Object.keys(current.values).length) {
        return current;
      }

      return {
        ...current,
        values: nextValues,
      };
    });

    if (test) {
      // Seed only missing mock values so mode changes survive group expansion.
      updateState((current) => ({
        ...current,
        ready: true,
        values: handles.reduce((accumulator, handle) => ({
          ...accumulator,
          [handle.name]: accumulator[handle.name] ?? currentValueForTestHandle(handle),
        }), { ...current.values }),
        error: null,
      }));

      return () => {};
    }

    let cancelled = false;
    const cache = getHandleCache(plcUrl);

    const setError = (error) => {
      if (cancelled) {
        return;
      }

      updateState((current) => ({
        ...current,
        error: error.message,
      }));
    };

    const resolveHandleIds = async () => {
      // Resolve only the paths that are not already cached for this PLC endpoint.
      const missingHandles = handles.filter((handle) => !cache.has(handle.name));

      if (missingHandles.length === 0) {
        handleIds.current = handles.reduce((accumulator, handle) => ({
          ...accumulator,
          [handle.name]: cache.get(handle.name),
        }), {});
        return;
      }

      if (!client.current) {
        client.current = new TcAdsWebService.Client(plcUrl, null, null);
      }

      const handleWriter = new TcAdsWebService.DataWriter();
      missingHandles.forEach((handle) => {
        handleWriter.writeDINT(TcAdsWebService.TcAdsReservedIndexGroups.SymbolHandleByName);
        handleWriter.writeDINT(0);
        handleWriter.writeDINT(4);
        handleWriter.writeDINT(handle.name.length);
      });
      missingHandles.forEach((handle) => {
        handleWriter.writeString(handle.name);
      });

      const response = await createReadWritePromise(client.current, {
        indexGroup: ADS_SUM_READ_WRITE,
        indexOffset: missingHandles.length,
        responseLength: (missingHandles.length * 4) + (missingHandles.length * 8),
        requestData: handleWriter.getBase64EncodedData(),
        timeoutMessage: 'PLC handle lookup timed out',
      });

      const { reader } = response;
      for (let index = 0; index < missingHandles.length; index += 1) {
        const errorCode = reader.readDWORD();
        reader.readDWORD();
        if (errorCode !== 0) {
          // Evict the whole cache so the next poll re-resolves all symbols from scratch.
          cache.clear();
          throw new Error(`Unable to resolve PLC handle "${missingHandles[index].name}"`);
        }
      }

      missingHandles.forEach((handle) => {
        cache.set(handle.name, reader.readDWORD());
      });

      handleIds.current = handles.reduce((accumulator, handle) => ({
        ...accumulator,
        [handle.name]: cache.get(handle.name),
      }), {});
    };

    const buildReadRequest = () => {
      // Sum-read packs all active PLC handles into one ADS request.
      const responseSize = handles.reduce(
        (total, handle) => total + getHandleSize(handle),
        0,
      );

      const readWriter = new TcAdsWebService.DataWriter();
      handles.forEach((handle) => {
        readWriter.writeDINT(TcAdsWebService.TcAdsReservedIndexGroups.SymbolValueByHandle);
        readWriter.writeDINT(handleIds.current[handle.name]);
        readWriter.writeDINT(getHandleSize(handle));
      });

      return {
        requestData: readWriter.getBase64EncodedData(),
        responseLength: responseSize + (handles.length * 4),
      };
    };

    const readOnce = async () => {
      if (cancelled) {
        return;
      }

      if (!client.current) {
        client.current = new TcAdsWebService.Client(plcUrl, null, null);
      }

      const { requestData, responseLength } = buildReadRequest();
      const response = await createReadWritePromise(client.current, {
        indexGroup: ADS_SUM_READ,
        indexOffset: handles.length,
        responseLength,
        requestData,
        timeoutMessage: 'PLC read timed out',
      });

      const nextValues = {};
      const { reader } = response;

      // TwinCAT returns one ADS error code per requested handle before the values.
      for (let index = 0; index < handles.length; index += 1) {
        const errorCode = reader.readDWORD();
        if (errorCode !== 0) {
          // A non-zero code means the handle ID is stale (PLC redeployed). Evict so
          // the next poll re-resolves all symbols before attempting another read.
          cache.clear();
          throw new Error(`Unable to read PLC handle "${handles[index].name}"`);
        }
      }

      handles.forEach((handle) => {
        nextValues[handle.name] = readHandleValue(reader, handle);
      });

      if (cancelled) {
        return;
      }

      updateState((current) => ({
        ...current,
        ready: true,
        values: {
          ...current.values,
          ...nextValues,
        },
        error: null,
      }));
    };

    const scheduleNextPoll = () => {
      if (cancelled) {
        return;
      }

      // Schedule the next poll only after the current read settles.
      pollTimeoutId.current = window.setTimeout(async () => {
        try {
          await resolveHandleIds();
          await readOnce();
        } catch (error) {
          setError(error);
        } finally {
          scheduleNextPoll();
        }
      }, ADS_POLL_INTERVAL_MS);
    };

    (async () => {
      updateState((current) => ({
        ...current,
        error: null,
      }));

      try {
        await resolveHandleIds();
        await readOnce();
      } catch (error) {
        setError(error);
      } finally {
        scheduleNextPoll();
      }
    })();

    return () => {
      cancelled = true;

      if (pollTimeoutId.current) {
        window.clearTimeout(pollTimeoutId.current);
        pollTimeoutId.current = null;
      }
    };
  }, [handles, plcUrl, test, updateState]);

  const write = useCallback(async ({ handle, value }) => {
    const performWrite = async () => {
      if (test) {
        updateState((current) => ({
          ...current,
          values: {
            ...current.values,
            [handle]: value,
          },
          error: null,
        }));
        return;
      }

      const handleConfig = handlesByName[handle];
      if (!handleConfig) {
        throw new Error(`Unknown PLC handle "${handle}"`);
      }

      const handleId = handleIds.current[handle];
      if (!handleId || !client.current) {
        throw new Error(`PLC handle "${handle}" is not ready`);
      }

      const size = getHandleSize(handleConfig, value);
      const writer = new TcAdsWebService.DataWriter();

      // Writes target a resolved symbol handle, not the original PLC path string.
      writer.writeDINT(TcAdsWebService.TcAdsReservedIndexGroups.SymbolValueByHandle);
      writer.writeDINT(handleId);
      writer.writeDINT(size);
      writeHandleValue(writer, handleConfig, value, size);

      await createReadWritePromise(client.current, {
        indexGroup: ADS_SUM_WRITE,
        indexOffset: 1,
        responseLength: size + 4,
        requestData: writer.getBase64EncodedData(),
        timeoutMessage: `PLC write timed out for "${handle}"`,
      });

      updateState((current) => ({
        ...current,
        values: {
          ...current.values,
          // Mirror the write locally so the UI updates immediately.
          [handle]: value,
        },
        error: null,
      }));
    };

    // Chain writes onto one promise queue so PLC commands stay ordered.
    const queuedWrite = writeQueue.current.then(performWrite);
    writeQueue.current = queuedWrite.catch(() => {});

    try {
      await queuedWrite;
    } catch (error) {
      updateState((current) => ({
        ...current,
        error: error.message,
      }));
      throw error;
    }
  }, [handlesByName, test, updateState]);

  const get = useCallback(() => state.current, []);
  const subscribe = useCallback((listener) => {
    listeners.current.add(listener);

    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  return useMemo(() => ({
    get,
    subscribe,
    write,
  }), [get, subscribe, write]);
}
